import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Typography } from '@mui/material';
import { Save as SaveIcon, Refresh as RefreshIcon, RestartAlt as RestartAltIcon } from '@mui/icons-material';
import { useAuth0Token } from '../../../hooks/useAuth0Token';
import { getAdvisorCaseAssumptions, updateAdvisorCaseAssumptions } from '../../../services/advisorConfigService';
import { AdvisorCaseAssumptionsResponse, CaseAssumptions } from '../../../types/advisor';
import CaseAssumptionsForm, { buildFallbacks, hasAnyAssumptions, toUpdate } from './CaseAssumptionsForm';

interface AdvisorCaseAssumptionsEditorProps {
  customerId: string;
  onSave?: () => void;
}

const SOURCE_LABEL: Record<AdvisorCaseAssumptionsResponse['source'], string> = {
  advisor: 'Customized for this advisor',
  firm: 'Inherited from firm',
  default: 'PlanScout defaults',
};

/**
 * Per-advisor editor for case assumptions — the account returns (by tax bucket),
 * COLA / inflation and taxable-account tax treatment stamped on every new case for
 * this advisor (types/planningAssumptions.ts is the catalog). Edits the
 * advisor's OWN keys; anything left blank falls through to the firm/branch
 * value (shown beneath each field) and then to the PlanScout baseline.
 * Resolution is live, so "Use firm defaults" is a real reset: it clears the
 * advisor's keys rather than copying the firm's values.
 *
 * Saves to planscout-api (advisorConfigs.sipsConfig), not to SIPS — these are
 * PlanScout case-creation assumptions, distinct from the SIPS automation
 * preferences' time-horizon bucket returns shown above it.
 */
const AdvisorCaseAssumptionsEditor: React.FC<AdvisorCaseAssumptionsEditorProps> = ({ customerId, onSave }) => {
  const { token } = useAuth0Token();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<AdvisorCaseAssumptionsResponse | null>(null);
  const [edited, setEdited] = useState<CaseAssumptions>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const apply = (res: AdvisorCaseAssumptionsResponse) => {
    setData(res);
    setEdited({ ...(res.own || {}) });
    setHasChanges(false);
  };

  const load = useCallback(async () => {
    if (!token || !customerId) return;
    setLoading(true);
    setError(null);
    try {
      apply(await getAdvisorCaseAssumptions(token, customerId));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load case assumptions');
    } finally {
      setLoading(false);
    }
  }, [token, customerId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleChange = (next: CaseAssumptions) => {
    setEdited(next);
    setHasChanges(true);
    setSaveSuccess(false);
  };

  const handleDiscard = () => {
    if (data) setEdited({ ...(data.own || {}) });
    setHasChanges(false);
  };

  // Empties the advisor's own keys; Save then clears them (PUT nulls) so the
  // firm/branch values apply again.
  const handleUseFirmDefaults = () => {
    setEdited({});
    setHasChanges(true);
    setSaveSuccess(false);
  };

  const handleSave = async () => {
    if (!token) return;
    setSaving(true);
    setSaveError(null);
    try {
      apply(await updateAdvisorCaseAssumptions(token, customerId, toUpdate(edited)));
      setSaveSuccess(true);
      onSave?.();
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err: any) {
      const details = err.response?.data?.details;
      setSaveError(Array.isArray(details) ? details.join('; ') : err.response?.data?.message || 'Failed to save case assumptions');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !data) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        {error ? (
          <Alert severity="error">
            {error}
            <Button size="small" onClick={load} sx={{ ml: 2 }}>Retry</Button>
          </Alert>
        ) : (
          <CircularProgress />
        )}
      </Box>
    );
  }

  const inheritedLabel = data.firmName ? `${data.firmName} default` : 'Firm default';

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Typography variant="h6">Case Assumptions</Typography>
          <Chip
            size="small"
            label={SOURCE_LABEL[data.source]}
            color={data.source === 'advisor' ? 'primary' : 'default'}
            variant="outlined"
          />
        </Box>
        <Box display="flex" gap={1.5} flexWrap="wrap">
          {data.firmId && (
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<RestartAltIcon />}
              onClick={handleUseFirmDefaults}
              disabled={saving || !hasAnyAssumptions(edited)}
            >
              Use Firm Defaults
            </Button>
          )}
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleDiscard} disabled={!hasChanges || saving}>
            Discard
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={!hasChanges || saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        The account returns, COLA / inflation and taxable-account taxes used when this advisor's cases are created. Blank fields use the
        {data.firmId ? ' firm (or branch) value shown beneath them, then the' : ''} PlanScout default.
        These are PlanScout case-creation assumptions — separate from the SIPS time-horizon bucket
        returns above.
      </Typography>

      {(saveError || saveSuccess) && (
        <Box mb={2}>
          {saveError && <Alert severity="error" onClose={() => setSaveError(null)}>{saveError}</Alert>}
          {saveSuccess && <Alert severity="success">Case assumptions saved!</Alert>}
        </Box>
      )}

      <CaseAssumptionsForm
        value={edited}
        onChange={handleChange}
        fallbacks={buildFallbacks(data.inherited, data.defaults, inheritedLabel)}
        disabled={saving}
      />
    </Box>
  );
};

export default AdvisorCaseAssumptionsEditor;
